<template>
    <main class="dashboard">
        <RockSpinner v-if="loadingMonthBonus || loading" />
        <div class="container">
            <div class="row d-flex flex-sm-row-reverse">
                <div class="col-md-12 col-lg-6 offset-lg-6">
                    <div v-if="isManager" class="col-24">
                        <RockEmployeeSelection v-model="selectedEmployee" />
                    </div>
                    <div v-if="selectedYear" class="col-24">
                        <RockYearSelection
                            v-if="serviceYears"
                            :years="serviceYears"
                            :selected="selectedYear"
                            @change="(year) => selectServiceYear(year)"
                        />
                    </div>
                    <div class="col-24">
                        <button
                            class="btn btn-secondary no-arrow w-100"
                            :disabled="downloading"
                            @click="handleDownload"
                        >
                            Download jaaroverzicht
                        </button>
                    </div>
                    <div
                        v-if="isManager && selectedEmployee"
                        class="mt-3 text-right"
                    >
                        <button
                            v-if="cacheClearIdle"
                            class="btn btn-link text-light btn-small"
                            @click="clearCache"
                        >
                            Clear employee cache
                        </button>
                        <small v-else>Employee cache cleared!</small>
                    </div>
                </div>
                <div class="col-md-12">
                    <RockIntro
                        v-if="!loading"
                        :year-bonus="yearBonus"
                        :final="!yearBonus.final"
                    />
                </div>
            </div>
            <div
                v-if="!loading"
                class="row justify-content-end legend"
                :class="{ negMargin: !isManager }"
            >
                <span
                    class="col-12 col-sm-12 col-lg-4 icon icon-legend icon-expected-bonus"
                    >Geschatte bonus</span
                >
                <span
                    class="col-12 col-sm-12 col-lg-4 icon icon-legend icon-bonus"
                    >Behaalde bonus</span
                >
            </div>
            <div class="row">
                <div class="col-24">
                    <RockChart
                        v-if="!loading"
                        :bonusses="bonusses"
                        :selected-bonus="selectedBonus"
                        :year="selectedYear"
                    />
                </div>
            </div>
        </div>
        <RockOverview
            v-if="!loading && !loadingMonthBonus"
            :selected-bonus="selectedBonus"
            :selected-month-bonus="selectedMonthBonus"
            :selected-employee="selectedEmployee"
            :is-manager="isManager"
            @save="(updatedData) => save(updatedData)"
        />
    </main>
</template>
<script>
import RockIntro from '@/components/RockIntro';
import RockDownload from '@/mixins/RockDownload';
import RockYearSelection from '@/components/RockYearSelection';
import RockEmployeeSelection from '@/components/RockEmployeeSelection';
import RockOverview from '@/components/RockOverview';
import RockChart from '@/components/RockChart';
import RockSpinner from '@/components/RockSpinner';
import EventBus from '@/shared/VueEvent';

export default {
    name: 'RockDashboard',
    components: {
        RockIntro,
        RockYearSelection,
        RockEmployeeSelection,
        RockChart,
        RockSpinner,
        RockOverview
    },
    mixins: [RockDownload],
    data() {
        return {
            bonusses: [],
            yearBonus: [],
            serviceYears: [],
            employees: [],
            selectedYear: null,
            selectedBonus: null,
            selectedMonthBonus: null,
            loading: true,
            loadingMonthBonus: false,
            selectedEmployee: null,
            isManager: false,
            cacheClearIdle: true
        };
    },
    computed: {
        isEditable: function () {
            return (
                !this.isManager &&
                this.selectedBonus &&
                !this.selectedBonus.final
            );
        }
    },
    watch: {
        selectedEmployee(newValue, oldValue) {
            if (oldValue && newValue.employeeId !== oldValue.employeeId) {
                const oldSelectedYear = this.selectedYear;
                this.selectedEmployee = newValue;
                this.selectedYear = 0;
                this.selectedBonus = null;
                this.selectedMonthBonus = null;
                this.retrieveServiceYears().catch(() => {
                    this.selectedEmployee = oldValue;
                    this.selectedYear = oldSelectedYear;
                });
            }
        }
    },
    created() {
        this.isManager = this.$keycloak.hasUserRole('manager');
        if (this.isManager) {
            this.retrieveEmployees();
        } else {
            this.retrieveServiceYears();
        }
    },
    mounted() {
        EventBus.listen('selectBonus', (bonus) => {
            if (this.selectedBonus === bonus) {
                this.selectedBonus = null;
                this.selectedMonthBonus = null;
            } else {
                this.selectedBonus = bonus;
                this.selectedMonthBonus = null;
                this.changeMonthSelection(false);
            }
        });
        EventBus.listen('saveMessage', (message) => {
            this.saveMessage(message);
        });
        EventBus.listen('removeMessage', (id) => {
            this.removeMessage(id);
        });
    },
    methods: {
        clearCache: function () {
            const url = 'cache/' + this.selectedEmployee.employeeId;
            this.$http.delete(url).then(() => {
                const self = this;
                self.cacheClearIdle = false;
                setTimeout(() => {
                    self.cacheClearIdle = true;
                }, 3000);
            }, 3000);
        },
        retrieveEmployees: function () {
            this.loading = true;
            this.$http
                .get('employees')
                .then((response) => {
                    this.employees = response.data;
                    if (this.employees.length > 0) {
                        this.selectedEmployee = this.employees[0];
                    }
                    this.retrieveServiceYears();
                })
                .catch((error) => {
                    if (error.message !== 'ABORTED') {
                        this.loading = false;
                        console.log(error);
                    }
                });
        },
        retrieveServiceYears: function () {
            this.loading = true;
            const url = this.convertToEmployeeUrl('serviceyears');
            this.serviceYears = [];

            return this.$http
                .get(url)
                .then((response) => {
                    this.serviceYears = response.data;
                    this.selectedYear =
                        this.serviceYears[this.serviceYears.length - 1];
                    this.loading = false;
                    this.changeYearSelection();
                })
                .catch((error) => {
                    if (error.message !== 'ABORTED') {
                        this.loading = false;
                        console.log(error);
                    }
                    alert(
                        'De geselecteerde gebruiker kon niet worden geladen.'
                    );
                    return Promise.reject(error);
                });
        },
        async handleDownload() {
            let url = `serviceyears/${this.selectedYear.serviceYear}/export`;
            url = this.convertToEmployeeUrl(url);

            const startDate = this.formatDateToYear(
                this.selectedYear.startDate
            );

            let firstName;
            let lastName;

            if (this.isManager) {
                firstName = this.selectedEmployee.firstName;
                lastName = this.selectedEmployee.lastName;
            } else {
                const userInfo = await this.$keycloak.getUserInfo();
                firstName = userInfo.given_name;
                lastName = userInfo.family_name;
            }

            const fallbackFilename = `Bonusoverzicht ${firstName} ${lastName} ${startDate}`;
            this.downloadFile({ url, fallbackFilename });
        },
        selectServiceYear: function (year) {
            this.selectedYear = this.serviceYears[year - 1];
            this.selectedBonus = null;
            this.selectedMonthBonus = null;
            this.changeYearSelection();
        },
        changeYearSelection: function () {
            this.loading = true;
            let url = 'bonuses/' + this.selectedYear.serviceYear;
            url = this.convertToEmployeeUrl(url);

            this.$http
                .get(url)
                .then((response) => {
                    this.bonusses = response.data.bonuses;
                    this.yearBonus = {
                        amount: response.data.bonusAmount,
                        expectedAmount: response.data.expectedBonusAmount,
                        final: response.data.final,
                        charges: response.data.charges
                    };
                    this.loading = false;
                })
                .catch((error) => {
                    if (error.message !== 'ABORTED') {
                        this.loading = false;
                        console.log(error);
                    }
                });
        },
        changeMonthSelection: function (changeYear) {
            this.loadingMonthBonus = true;
            let url =
                'bonuses/' +
                this.selectedBonus.year +
                '/' +
                this.selectedBonus.month;
            url = this.convertToEmployeeUrl(url);

            this.$http
                .get(url)
                .then((response) => {
                    this.selectedMonthBonus = response.data;
                    this.loadingMonthBonus = false;
                    if (changeYear === true) {
                        this.changeYearSelection();
                    }
                })
                .catch((error) => {
                    if (error.message !== 'ABORTED') {
                        this.selectedMonthBonus = null;
                        this.loadingMonthBonus = false;
                        console.log(error);
                    }
                });
        },
        saveMessage: function (message) {
            let url = 'comments';
            if (message.id) {
                url = 'comments/' + message.id;
            }
            if (message.reply) {
                url = 'comments/' + message.reply + '/reply';
            }
            const payload = {
                comment: message.comment,
                year: this.selectedBonus.year,
                month: this.selectedBonus.month
            };
            url = this.convertToEmployeeUrl(url);

            this.$http
                .put(url, payload)
                .then(() => {
                    this.changeMonthSelection(false);
                })
                .catch(() => {
                    alert(
                        'Je bericht kon niet worden opgeslagen. Probeer het later opnieuw.'
                    );
                });
        },
        removeMessage: function (messageId) {
            let url = 'comments/' + messageId;
            url = this.convertToEmployeeUrl(url);

            this.$http
                .delete(url)
                .then(() => {
                    this.changeMonthSelection(false);
                })
                .catch(() => {
                    alert(
                        'Je bericht kon niet worden verwijderd. Probeer het later opnieuw.'
                    );
                });
        },
        save: function (updatedData) {
            const payload = {
                year: this.selectedBonus.year,
                month: this.selectedBonus.month,
                revenuesDetails: updatedData.updatedRevenues.revenuesDetails,
                costsDetails: updatedData.updatedCosts.costsDetails
            };

            this.$http
                .put('bonuses', payload)
                .then(() => {
                    EventBus.fire('toggleSuccessPopup', true);
                    this.changeMonthSelection(true);
                })
                .catch(() => {
                    alert(
                        'Je aanpassing kon niet worden opgeslagen. Probeer het later opnieuw.'
                    );
                });
        },
        formatDateToYear: function (date) {
            const formatedDate = new Date(date);
            return formatedDate.getFullYear();
        },
        convertToEmployeeUrl: function (url) {
            if (this.isManager && this.selectedEmployee) {
                url =
                    'employees/' + this.selectedEmployee.employeeId + '/' + url;
            }
            url = url + '?cb=' + new Date().getTime();
            return url;
        }
    }
};
</script>
